import type { GameEvent, GameState, PlaySummary, Side } from './contracts.js';

export const REGULATION_PERIODS = 4;
export const PERIOD_SECONDS = 720;
export const OVERTIME_SECONDS = 300;
const MAX_LAST_PLAYS = 5;

export function initialState(gameId: string): GameState {
  return {
    gameId,
    status: 'pre',
    asOfSeq: -1,
    period: 1,
    clockSec: PERIOD_SECONDS,
    homeScore: 0,
    awayScore: 0,
    leadChanges: 0,
    maxDeficit: { home: 0, away: 0 },
    players: {},
    lastPlays: [],
  };
}

/** Seconds left in regulation; in overtime, seconds left in the current OT period. */
export function secondsRemaining(period: number, clockSec: number): number {
  if (period > REGULATION_PERIODS) return clockSec;
  return (REGULATION_PERIODS - period) * PERIOD_SECONDS + clockSec;
}

/** Margin from the home team's perspective. */
export const margin = (s: Pick<GameState, 'homeScore' | 'awayScore'>): number =>
  s.homeScore - s.awayScore;

export const other = (side: Side): Side => (side === 'home' ? 'away' : 'home');

/** "Q4 2:18", "OT 0:45", "2OT 1:02". */
export function formatClock(period: number, clockSec: number): string {
  const label =
    period <= REGULATION_PERIODS
      ? `Q${period}`
      : period === REGULATION_PERIODS + 1
        ? 'OT'
        : `${period - REGULATION_PERIODS}OT`;
  const whole = Math.ceil(clockSec - 1e-9);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  const clock = clockSec < 60 && clockSec % 1 !== 0 ? clockSec.toFixed(1) : `${m}:${String(s).padStart(2, '0')}`;
  return `${label} ${clock}`;
}

/** Is game point (period, clockSec) at or past `cutoff`? */
export function isAtOrPast(
  at: { period: number; clockSec: number },
  cutoff: { period: number; clockSec: number },
): boolean {
  return at.period > cutoff.period || (at.period === cutoff.period && at.clockSec <= cutoff.clockSec);
}

const MEANINGFUL: ReadonlySet<GameEvent['type']> = new Set([
  'shot',
  'freethrow',
  'turnover',
  'steal',
  'block',
  'foul',
  'timeout',
  'period_end',
  'game_end',
]);

function summarize(e: GameEvent): PlaySummary | undefined {
  if (!MEANINGFUL.has(e.type)) return undefined;
  if (e.type === 'freethrow' && !e.made) return undefined;
  return {
    eventId: e.eventId,
    seq: e.seq,
    period: e.period,
    clockSec: e.clockSec,
    side: e.side,
    text: e.description,
    points: e.points,
  };
}

/**
 * Pure reducer: apply one event. Events must arrive in `seq` order; an event at or
 * before `asOfSeq` is ignored (idempotent under duplicate delivery).
 */
export function reduce(state: GameState, e: GameEvent): GameState {
  if (e.gameId !== state.gameId) throw new Error(`event ${e.eventId} is for another game`);
  if (e.seq <= state.asOfSeq) return state;

  const next: GameState = {
    ...state,
    asOfSeq: e.seq,
    status: e.type === 'game_end' ? 'final' : 'live',
    period: e.period,
    clockSec: e.clockSec,
    homeScore: e.homeScore,
    awayScore: e.awayScore,
    possession: e.possession ?? state.possession,
  };

  const m = e.homeScore - e.awayScore;
  const leader: Side | undefined = m > 0 ? 'home' : m < 0 ? 'away' : undefined;
  if (leader && state.lastLeader && leader !== state.lastLeader) next.leadChanges = state.leadChanges + 1;
  if (leader) next.lastLeader = leader;
  next.maxDeficit = {
    home: Math.max(state.maxDeficit.home, -m),
    away: Math.max(state.maxDeficit.away, m),
  };

  if (e.personId !== undefined && e.playerName && e.side && e.playerPoints !== undefined) {
    next.players = {
      ...state.players,
      [e.personId]: { personId: e.personId, name: e.playerName, side: e.side, points: e.playerPoints },
    };
  }

  const play = summarize(e);
  if (play) next.lastPlays = [play, ...state.lastPlays].slice(0, MAX_LAST_PLAYS);
  return next;
}

/** Fold events (already sorted by seq) into a state. */
export function stateAt(gameId: string, events: readonly GameEvent[], uptoSeq = Infinity): GameState {
  let s = initialState(gameId);
  for (const e of events) {
    if (e.seq > uptoSeq) break;
    s = reduce(s, e);
  }
  return s;
}

/** Top scorer for a side, if any. */
export function topScorer(s: GameState, side: Side) {
  let best: GameState['players'][number] | undefined;
  for (const p of Object.values(s.players)) if (p.side === side && (!best || p.points > best.points)) best = p;
  return best;
}
